import { useEffect, useRef, useState } from "react";

/**
 * Shared behaviour for canvas-pannable color picker popovers.
 *
 * Every color picker in the builder (shape colors, brand kit swatches,
 * Settings front/back background, etc.) needs the same three things on
 * top of MUI's Popover:
 *
 *   1. The canvas underneath must stay pannable while the picker is
 *      open — MUI's invisible Backdrop blocks the pointer-down that
 *      tldraw needs for two-finger pan. Callers should add
 *      `pointerEvents: "none"` on the Popover root + `hideBackdrop` +
 *      `disableScrollLock` to make that work; this hook complements
 *      that by replacing MUI's clickaway with a document-level
 *      mousedown listener (clickaway never fires once the Backdrop
 *      is hidden).
 *
 *   2. The Paper itself must be draggable by its "free border" —
 *      i.e. clicks on the padding around the picker content (where
 *      `e.target === e.currentTarget` on the wrapper div) start a
 *      drag. Clicks on descendants (color swatches, hex input, etc.)
 *      pass through normally. This replaces the earlier 6-dot handle
 *      that was bolted to the top-right corner.
 *
 *   3. Drag position resets on each open so each session starts at
 *      the anchored baseline (no "memory" of the previous drag).
 *
 * Performance: during a drag, every mousemove writes the two CSS
 * variables `--cp-drag-x` / `--cp-drag-y` directly to the Paper
 * element via `style.setProperty`, bypassing React. Callers consume
 * those variables inside their Paper transform — e.g.:
 *
 *   transform: "translate(calc(8px + var(--cp-drag-x, 0px)), var(--cp-drag-y, 0px))"
 *
 * Because the inline `transform` string never changes across renders
 * (the variables do all the work), React won't clobber the JS-set
 * values on unrelated re-renders. State is committed on mouseup so
 * the next render reflects the final position.
 *
 * Returns:
 *   - `paperContentRef`: ref for the wrapper div INSIDE the Paper.
 *     `.parentElement` is the Paper itself, which is where we write
 *     CSS variables.
 *   - `handleBorderMouseDown`: attach to the wrapper's `onMouseDown`.
 *     Only fires when the click lands on the padding (no descendant).
 *   - `isDragging`: used to flip the wrapper's cursor between grab /
 *     grabbing.
 *   - `dragOffset`: read-only state for callers that want to do
 *     post-drag bookkeeping; the rendered transform should rely on
 *     the CSS variables instead.
 */
export function useDraggableColorPicker(open: boolean, anchorEl: Element | null, onClose: () => void) {
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ startX: 0, startY: 0, originX: 0, originY: 0 });
  const paperContentRef = useRef<HTMLDivElement | null>(null);

  // Reset the drag offset (and the CSS variables on the Paper, if it
  // exists already from a previous open) every time the picker opens.
  // Otherwise the picker would reopen at wherever the user last
  // dragged it to, which felt random.
  useEffect(() => {
    if (!open) return;
    setDragOffset({ x: 0, y: 0 });
    const paperEl = paperContentRef.current?.parentElement as HTMLElement | null;
    if (paperEl) {
      paperEl.style.setProperty("--cp-drag-x", "0px");
      paperEl.style.setProperty("--cp-drag-y", "0px");
    }
  }, [open]);

  // Drag-to-move: while isDragging is true, listen on the window for
  // mousemove / mouseup. Direct DOM writes (not React state) so the
  // Paper tracks the cursor without re-rendering the whole popover
  // tree on every pixel of movement. MUI's Grow transition on Paper
  // is suppressed for the duration so the transform isn't animated.
  useEffect(() => {
    if (!isDragging) return;
    const paperEl = paperContentRef.current?.parentElement as HTMLElement | null;
    const previousTransition = paperEl?.style.transition ?? "";
    if (paperEl) paperEl.style.transition = "none";
    let latestX = dragStartRef.current.originX;
    let latestY = dragStartRef.current.originY;
    const onMove = (e: MouseEvent) => {
      const dx = e.clientX - dragStartRef.current.startX;
      const dy = e.clientY - dragStartRef.current.startY;
      latestX = dragStartRef.current.originX + dx;
      latestY = dragStartRef.current.originY + dy;
      if (paperEl) {
        paperEl.style.setProperty("--cp-drag-x", `${latestX}px`);
        paperEl.style.setProperty("--cp-drag-y", `${latestY}px`);
      }
    };
    const onUp = () => {
      if (paperEl) paperEl.style.transition = previousTransition;
      setIsDragging(false);
      setDragOffset({ x: latestX, y: latestY });
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [isDragging]);

  // Close-on-outside-click. Required because the pannable Popover has
  // pointerEvents:none + hideBackdrop, so MUI's built-in clickaway
  // never fires. We exclude the trigger element so clicks on the
  // anchor don't immediately close + reopen.
  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (paperContentRef.current && paperContentRef.current.contains(target)) return;
      if (anchorEl && anchorEl.contains(target)) return;
      onClose();
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open, anchorEl, onClose]);

  // Border-drag entry point. Only fires when the click target IS the
  // wrapper itself (the padding/border area) — clicks on descendants
  // bubble normally so swatches, inputs, etc. stay interactive.
  const handleBorderMouseDown = (e: React.MouseEvent) => {
    if (e.target !== e.currentTarget) return;
    e.preventDefault();
    e.stopPropagation();
    dragStartRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      originX: dragOffset.x,
      originY: dragOffset.y,
    };
    setIsDragging(true);
  };

  return { paperContentRef, handleBorderMouseDown, isDragging, dragOffset };
}
